import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Newspaper, ChevronRight, Shield, Clock } from "lucide-react";
import { tierColors } from "../../../utils/credibilityTiers";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

// Structurally mirrors RelevantGroups.jsx (groupPageComponents/RelevantGroups.jsx)
// deliberately -- same header/list/footer shape, same bg-gray-800/50 card
// convention as the rest of ConsolidatedSocialSection -- since this panel
// sits directly alongside it and is meant to read as "the same kind of
// thing" (people/things you follow), not a visually distinct one-off.
//
// `journalists`/`loading` are passed in rather than self-fetched (2026-08-30)
// -- ConsolidatedSocialSection needs to know this panel's empty state
// *before* render, to decide whether to show it standalone or folded into
// SocialDiscoveryEmptyState alongside Trending Conversations, so the parent
// now owns the single fetch and both consumers read the same store slice.
const FollowedJournalistsPanel = ({ journalists = [], loading = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!loading && journalists.length === 0) {
    return (
      <div className="bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-700/50 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
          <Newspaper className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          {t('dashboard.followedJournalists.title')}
        </h3>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-cream-200 dark:bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            {t('dashboard.followedJournalists.emptyMessage')}
          </p>
          <button
            onClick={() => navigate("/news")}
            className="px-4 py-2 bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 rounded-lg text-sm text-violet-700 dark:text-violet-300 transition-all inline-flex items-center gap-2"
          >
            <Newspaper className="w-4 h-4" />
            {t('dashboard.followedJournalists.browseNews')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-700/50 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-cream-300 dark:border-gray-700/50 bg-cream-100 dark:bg-gray-800/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              {t('dashboard.followedJournalists.title')}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{t('dashboard.followedJournalists.subtitle')}</p>
          </div>
          <button
            onClick={() => navigate("/news?sourceType=elite_original")}
            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
          >
            {t('dashboard.followedJournalists.viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="divide-y divide-cream-300 dark:divide-gray-700/50">
        {loading && journalists.length === 0
          ? [1, 2].map((i) => <div key={i} className="p-4 h-16 animate-pulse bg-gray-700/20" />)
          : journalists.map((j) => (
              <JournalistCard key={j.id} journalist={j} onClick={() => navigate(`/public-profiles/${encodeURIComponent(j.username)}`)} />
            ))}
      </div>
    </div>
  );
};

const JournalistCard = ({ journalist, onClick }) => {
  const { t } = useTranslation();
  const colors = tierColors(journalist.tier);
  const initial = (journalist.username || "?")[0].toUpperCase();

  return (
    <div onClick={onClick} className="p-4 hover:bg-cream-900/5 dark:hover:bg-gray-700/30 cursor-pointer transition-all duration-200 group">
      <div className="flex items-start gap-3">
        {journalist.profile_picture ? (
          <img
            src={resolveMediaUrl(journalist.profile_picture)}
            alt={`${journalist.username}'s avatar`}
            className="w-10 h-10 rounded-full object-cover shadow-lg flex-shrink-0"
          />
        ) : (
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 bg-gradient-to-br ${colors.gradient}`}>
            <span className="text-sm font-bold text-white">{initial}</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors">
              {journalist.username}
            </h4>
            <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${colors.bg} ${colors.text} border ${colors.border} flex-shrink-0`}>
              <Shield size={9} /> {journalist.tier}
            </span>
          </div>

          {journalist.latest_article ? (
            <div className="min-w-0">
              <p className="text-xs text-gray-600 dark:text-gray-300 truncate">{journalist.latest_article.title}</p>
              {journalist.latest_article.published_at && (
                <span className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">
                  <Clock size={10} />
                  {new Date(journalist.latest_article.published_at).toLocaleDateString()}
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-500 italic">{t('dashboard.followedJournalists.noArticlesYet')}</p>
          )}
        </div>

        <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors flex-shrink-0" />
      </div>
    </div>
  );
};

export default FollowedJournalistsPanel;
